import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatTimezoneLabel } from '@/lib/format/timezone';

export const BR_TIMEZONES = [
  { value: 'UTC', label: 'UTC — Coordinated Universal Time (UTC+0)' },
  { value: 'America/Sao_Paulo', label: `${formatTimezoneLabel('America/Sao_Paulo')} — Brasília / São Paulo (UTC-3)` },
  { value: 'America/Belem', label: 'America/Belem — Belém / Pará (UTC-3)' },
  { value: 'America/Fortaleza', label: 'America/Fortaleza — Fortaleza / Ceará (UTC-3)' },
  { value: 'America/Recife', label: 'America/Recife — Recife / Pernambuco (UTC-3)' },
  { value: 'America/Maceio', label: 'America/Maceio — Maceió / Alagoas (UTC-3)' },
  { value: 'America/Bahia', label: 'America/Bahia — Salvador / Bahia (UTC-3)' },
  { value: 'America/Campo_Grande', label: 'America/Campo_Grande — Campo Grande / MS (UTC-4)' },
  { value: 'America/Cuiaba', label: 'America/Cuiaba — Cuiabá / Mato Grosso (UTC-4)' },
  { value: 'America/Manaus', label: 'America/Manaus — Manaus / Amazonas (UTC-4)' },
  { value: 'America/Porto_Velho', label: 'America/Porto_Velho — Porto Velho / Rondônia (UTC-4)' },
  { value: 'America/Boa_Vista', label: 'America/Boa_Vista — Boa Vista / Roraima (UTC-4)' },
  { value: 'America/Rio_Branco', label: 'America/Rio_Branco — Rio Branco / Acre (UTC-5)' },
  { value: 'America/Noronha', label: 'America/Noronha — Fernando de Noronha (UTC-2)' },
];

interface TimezoneSelectProps {
  value: string;
  onChange: (val: string) => void;
}

export const TimezoneSelect = ({ value, onChange }: TimezoneSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = BR_TIMEZONES.find(tz => tz.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal text-xs h-9 px-3"
        >
          <span className="truncate">{formatTimezoneLabel(selected?.value ?? value) || t('admin.challenges.prizeBuilder.timezonePlaceholder')}</span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t('admin.challenges.prizeBuilder.timezoneSearch')} className="text-xs" />
          <CommandList className="max-h-52">
            <CommandEmpty>{t('admin.challenges.prizeBuilder.timezoneNotFound')}</CommandEmpty>
            <CommandGroup>
              {BR_TIMEZONES.map(tz => (
                <CommandItem
                  key={tz.value}
                  value={tz.label}
                  keywords={[tz.value]}
                  onSelect={() => { onChange(tz.value); setOpen(false); }}
                  className="text-xs"
                >
                  <Check className={cn('mr-2 h-3 w-3', value === tz.value ? 'opacity-100' : 'opacity-0')} />
                  {tz.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
